import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Stack,
    Paper,
    Divider,
    Typography,
    Card,
    CardContent,
    IconButton,
} from '@mui/material';
import { request } from '../../Utils/request';
import MediaUpload from '../../Components/MediaUpload';
import { extractHashtags } from '../../services/CommonFunctions';
import CategoryChips from '../../Components/CategoryChips';
import MapSelector from '../../Components/MapSelector';
import { getSignedUploadUrls, uploadFileToSignedUrl } from '../../services/API/Api';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { fetchCategories } from '../../services/CommonApiCall';
import { fileToBase64, base64ToFile } from '../../Utils/base64Converter';
import { revokeBlobUrls } from '../../Utils/blobUrlUtils';
import { placeDraftUtils } from '../../Utils/placeDraftStorage';
import { ReactComponent as DraftIcon } from "../../Icons/svg/draft.svg";
import TopNavBar from '../../Components/TopNavigation/TopNavBar';


interface PlaceValidationErrors {
    location?: string;
    images?: string;
    name?: string;
    description?: string;
    address?: string;
    categories?: string;
    email?: string;
    ownerName?: string;
    phone?: string;
    whatsapp?: string;
    /** Non-field / server errors */
    general?: string;
}

interface PlaceFormData {
    name: string;
    location: {
        lat: number | string;
        lng: number | string;
    };
    files: Array<{
        url: string;
        fileType: string;
        featured: boolean;
    }>;
    description: string;
    categories: string[];
    tags: string[];
    address: string
    social: { key: string; value: string }[];
    owner_details: {
        name: string;
        email: string;
        phone: string;
        whatsapp: string;
    };
    // type: string;
}





const PlaceForm: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const emptyForm = {
        name: '',
        location: {
            lat: '',
            lng: '',
        },
        files: [],
        description: '',
        categories: [],
        tags: [],
        address: '',
        social: [
            { key: 'website', value: '' },
            { key: 'facebook', value: '' },
            { key: 'instagram', value: '' },
            { key: 'youtube', value: '' }
        ],
        owner_details: {
            name: '',
            email: '',
            phone: '',
            whatsapp: ''
        },
        // type: '',
    };
    const navigate = useNavigate();
    const [form, setForm] = useState<PlaceFormData>(emptyForm);
    // const [categories, setCategories] = useState<Category[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [imagesData, setImagesData] = useState<any[]>([])
    const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [currentLocation, setCurrentLocation] = useState<{ lat: number, lng: number, address: string } | null>(null);
    const [validationErrors, setValidationErrors] = useState<PlaceValidationErrors>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const categories = useSelector((state: RootState) => state.category.categories);
    const placeDrafts = useSelector((state: RootState) => state.placeDraft?.drafts || []);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const numbersOnlyRegex = /^\d+$/;

    useEffect(() => {
        fetchCategories(dispatch, 'post-places');
    }, []);


    // Handle draft loading from navigation state
    useEffect(() => {
        const location = window.location;
        const urlParams = new URLSearchParams(location.search);
        const draftId = urlParams.get('draftId');
        const isPreview = urlParams.get('preview') === 'true';

        if (draftId) {
            handleLoadDraft(draftId);
            if (isPreview) {
                // Handle preview mode if needed
                console.log('Preview mode for draft:', draftId);
            }
        }
    }, []);


    const handleDescriptionBlur = () => {
        const hashtags = extractHashtags(form.description);
        if (hashtags.length > 0) {
            setForm(prev => ({
                ...prev,
                tags: Array.from(new Set([...prev.tags, ...hashtags]))
            }));
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (name === 'name') setValidationErrors((prev) => ({ ...prev, name: undefined }));
        if (name === 'description') setValidationErrors((prev) => ({ ...prev, description: undefined }));
        if (name === 'address') setValidationErrors((prev) => ({ ...prev, address: undefined }));
    };



    const handleLocationSelect = (lat: number, lng: number, address: string) => {
        setForm(prev => ({
            ...prev,
            location: {
                lat: lat,
                lng: lng,
            }
        }));
        setCurrentLocation({ lat, lng, address });
        setValidationErrors((prev) => ({ ...prev, location: undefined }));
    };

    const validatePlaceForm = (): PlaceValidationErrors => {
        const errors: PlaceValidationErrors = {};

        if (!form.name || form.name.trim() === '') {
            errors.name = 'Place name is required';
        } else if (form.name.trim().length > 100) {
            errors.name = 'Place name must be 100 characters or less';
        }

        if (!form.description || form.description.trim() === '') {
            errors.description = 'Description is required';
        }

        if (!form.address || form.address.trim() === '') {
            errors.address = 'Address is required';
        }

        if (!form.location.lat || !form.location.lng || form.location.lat === '' || form.location.lng === '') {
            errors.location = 'Please select a location on the map';
        }

        if (!form.categories || form.categories.length === 0) {
            errors.categories = 'Please select at least one category';
        }

        if (!imagesData || imagesData.length === 0) {
            errors.images = 'Please upload at least one image';
        }

        if (!form.owner_details.email || form.owner_details.email.trim() === '') {
            errors.email = 'Email is required';
        } else if (!emailRegex.test(form.owner_details.email.trim())) {
            errors.email = 'Please enter a valid email address';
        }

        if (!form.owner_details.name || form.owner_details.name.trim() === '') {
            errors.ownerName = 'Owner name is required';
        }

        if (!form.owner_details.phone || form.owner_details.phone.trim() === '') {
            errors.phone = 'Phone is required';
        } else if (!numbersOnlyRegex.test(form.owner_details.phone.trim())) {
            errors.phone = 'Phone must contain numbers only';
        }

        if (!form.owner_details.whatsapp || form.owner_details.whatsapp.trim() === '') {
            errors.whatsapp = 'WhatsApp is required';
        } else if (!numbersOnlyRegex.test(form.owner_details.whatsapp.trim())) {
            errors.whatsapp = 'WhatsApp must contain numbers only';
        }

        return errors;
    };

    // Draft handling functions
    const handleSaveDraft = async () => {
        try {
            setIsSavingDraft(true);

            // Convert File objects to base64 when saving
            const serializedImagesData = await Promise.all(imagesData.map(async img => ({
                url: img.url,
                caption: img.caption || '',
                featured: img.featured || false,
                fileBase64: img.file ? await fileToBase64(img.file) : null,
                fileName: img.file?.name || '',
                fileType: img.file?.type || '',
                fileSize: img.file?.size || 0
            })));

            const draftData = {
                name: form.name,
                location: form.location,
                address: form.address,
                description: form.description,
                categories: form.categories,
                tags: form.tags,
                social: form.social,
                owner_details: form.owner_details,
                imagesData: serializedImagesData,
            };

            if (currentDraftId) {
                placeDraftUtils.updateDraft(dispatch, currentDraftId, draftData);
                alert('Draft updated successfully!');
            } else {
                placeDraftUtils.saveDraft(dispatch, draftData);
                setCurrentDraftId(Date.now().toString());
                alert('Draft saved successfully!');
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            alert('Failed to save draft. Please try again.');
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleLoadDraft = (draftId: string) => {
        const draft = placeDrafts.find(d => d.id === draftId);
        if (draft) {
            setCurrentDraftId(draftId);
            setForm({
                name: draft.name,
                location: draft.location,
                address: draft.address,
                description: draft.description,
                categories: draft.categories,
                tags: draft.tags,
                social: draft.social,
                owner_details: draft.owner_details,
                files: [],
            });
            setSelectedCategories(draft.categories);

            // Set current location for map display if location data exists
            if (draft.location.lat && draft.location.lng && draft.address) {
                setCurrentLocation({
                    lat: typeof draft.location.lat === 'string' ? parseFloat(draft.location.lat) : draft.location.lat,
                    lng: typeof draft.location.lng === 'string' ? parseFloat(draft.location.lng) : draft.location.lng,
                    address: draft.address
                });
            }

            // Convert base64 back to File objects when loading
            const reconstructedImagesData = (draft.imagesData || []).map((img: any) => {
                const newFile = img.fileBase64 ? base64ToFile(img.fileBase64, img.fileName, img.fileType) : null;
                return {
                    url: newFile ? URL.createObjectURL(newFile) : img.url,
                    caption: img.caption,
                    featured: img.featured,
                    file: newFile
                };
            });

            setImagesData(reconstructedImagesData);
        }
    };

    useEffect(() => {
        return () => {
            revokeBlobUrls(imagesData);
        };
    }, [imagesData]);

    const handleDeleteDraft = (draftId: string) => {
        placeDraftUtils.deleteDraft(dispatch, draftId);
        if (currentDraftId === draftId) {
            setCurrentDraftId(null);
            setForm(emptyForm);
            setImagesData([]);
            setSelectedCategories([]);
            setCurrentLocation(null);
            setValidationErrors({});
            setSubmitAttempted(false);
        }
    };



    const mapBackendPathToFieldKey = (fieldPath: string): keyof PlaceValidationErrors | null => {
        const p = fieldPath || '';
        if (p.includes('owner_details')) {
            const part = p.split('.').pop() || '';
            if (part === 'email') return 'email';
            if (part === 'name') return 'ownerName';
            if (part === 'phone') return 'phone';
            if (part === 'whatsapp') return 'whatsapp';
            return null;
        }
        if (p.includes('location')) return 'location';
        if (p === 'name' || p.endsWith('.name')) return 'name';
        if (p.includes('description')) return 'description';
        if (p.includes('address')) return 'address';
        if (p.includes('categories')) return 'categories';
        if (p.includes('files')) return 'images';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log('handleSubmit', form);

        setSubmitAttempted(true);
        const fieldErrors = validatePlaceForm();
        if (Object.keys(fieldErrors).length > 0) {
            setValidationErrors(fieldErrors);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setValidationErrors({});

        const mediaFiles = imagesData;

        // try {
            // Step 1: Prepare filesProp for all media
            const filesProp = mediaFiles.map((item: any) => ({
                folder: 'place',
                fileType: item.file.type,
            }));

            // Step 2: Get signed upload URLs for all files
            const signedUrlsData = await getSignedUploadUrls(filesProp);
            const signedFiles = signedUrlsData?.data?.files || [];

            if (signedFiles.length !== mediaFiles.length) {
                console.error('Mismatch in signed URLs and media files count');
                return;
            }

            // Step 3: Upload each file and collect file metadata
            const uploadedFiles: any[] = [];

            for (let i = 0; i < mediaFiles.length; i++) {
                const file = mediaFiles[i].file;
                const { uploadUrl, fileKey } = signedFiles[i];

                if (!uploadUrl) {
                    console.error(`No upload URL for file ${i}`);
                    continue;
                }

                const uploadResponse = await uploadFileToSignedUrl(file, uploadUrl);

                if (uploadResponse.status === 200) {
                    uploadedFiles.push({
                        fileKey,
                        fileType: file.type,
                        featured: i === 0, // Optional: first image/video is featured
                    });
                } else {
                    console.error(`Upload failed for file ${i}`);
                }
            }

            if (uploadedFiles.length === 0) {
                console.error('No files were uploaded successfully');
                return;
            }

            // Step 4: Add uploaded file info to the form state
            setForm(prev => ({
                ...prev,
                files: uploadedFiles
            }));



            try {
                const response = await request('/places', {
                    method: 'POST',
                    body: {
                        ...form,
                        files: uploadedFiles,
                    },
                    authorization: true,
                });
                
                alert('Place added successfully!');
                setForm(emptyForm);
    
                /**
                 * STACK CLEAR REQUIRED HERE:
                 * After successful place submission, we replace the input form page in history to:
                 * 1. Prevent users from navigating back to the place form after successful submission
                 * 2. Avoid accidental duplicate place creation if user hits back button
                 * 3. Provide clean navigation flow to the all places listing page
                 * 4. Clear potentially large image/media data from form history
                 * 5. Follow standard content creation UX patterns
                 */
                navigate('/allplaces', { replace: true });
            } catch (err: any) {
                setSubmitAttempted(true);
                const errorData = err?.errorData || err?.response || {};
                const apiValidationErrors = err?.errors || errorData?.errors || [];

                const formatFieldName = (fieldPath: string): string => {
                    if (fieldPath.includes('owner_details')) {
                        const fieldPart = fieldPath.split('.').pop() || '';
                        return `Owner ${fieldPart.charAt(0).toUpperCase() + fieldPart.slice(1)}`;
                    } else if (fieldPath.includes('social')) {
                        const socialMatch = fieldPath.match(/social\[(\d+)\]/);
                        if (socialMatch) {
                            const socialIndex = parseInt(socialMatch[1], 10);
                            const socialKey = form.social[socialIndex]?.key || 'social';
                            return socialKey.charAt(0).toUpperCase() + socialKey.slice(1);
                        } else if (fieldPath.includes('value')) {
                            return 'Social Media';
                        }
                        return 'Social Media';
                    } else if (fieldPath.includes('location')) {
                        const locationPart = fieldPath.split('.').pop() || '';
                        return `Location ${locationPart === 'lat' ? 'Latitude' : locationPart === 'lng' ? 'Longitude' : locationPart}`;
                    } else {
                        return fieldPath
                            .replace(/([A-Z])/g, ' $1')
                            .replace(/^./, (str: string) => str.toUpperCase())
                            .trim();
                    }
                };

                const nextErrors: PlaceValidationErrors = {};
                const unmappedMessages: string[] = [];

                if (Array.isArray(apiValidationErrors) && apiValidationErrors.length > 0) {
                    (apiValidationErrors as any[]).forEach((validationErr: any) => {
                        const path = validationErr.path || '';
                        const msg = validationErr.msg || validationErr.message || 'Invalid value';
                        const key = mapBackendPathToFieldKey(path);
                        if (key) {
                            nextErrors[key] = msg;
                        } else {
                            unmappedMessages.push(`${formatFieldName(path)}: ${msg}`);
                        }
                    });
                }

                const errorText =
                    (typeof err?.message === 'string' && err.message) ||
                    (typeof errorData?.error === 'string' && errorData.error) ||
                    '';

                if (unmappedMessages.length > 0) {
                    nextErrors.general = unmappedMessages.join('\n');
                } else if (Object.keys(nextErrors).filter((k) => k !== 'general').length === 0 && errorText) {
                    if (errorText.includes('Path `') && errorText.includes(' is required')) {
                        const cleanErrorText = errorText.replace(/^Validation failed:\s*/i, '');
                        const errorPattern = /([\w.\[\]]+):\s*Path\s*`[^`]+`\s*is\s*required\.?/g;
                        const matches = Array.from(cleanErrorText.matchAll(errorPattern));
                        const seenFields = new Set<string>();
                        for (const match of matches) {
                            const fieldPath = (match as RegExpMatchArray)[1].trim();
                            if (seenFields.has(fieldPath)) continue;
                            seenFields.add(fieldPath);
                            const msg = 'This field is required';
                            const key = mapBackendPathToFieldKey(fieldPath);
                            if (key) {
                                nextErrors[key] = nextErrors[key] || msg;
                            } else {
                                unmappedMessages.push(`${formatFieldName(fieldPath)}: ${msg}`);
                            }
                        }
                        if (unmappedMessages.length > 0) {
                            nextErrors.general = unmappedMessages.join('\n');
                        } else if (matches.length === 0) {
                            nextErrors.general = errorText;
                        }
                    } else {
                        nextErrors.general = errorText;
                    }
                } else if (Object.keys(nextErrors).filter((k) => k !== 'general').length === 0 && !nextErrors.general) {
                    nextErrors.general = errorText || 'Failed to create place. Please try again.';
                }

                setValidationErrors(nextErrors);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
         
        // } catch (error: any) {
        //     console.error('Submission failed:', error);
            
        //     // Extract error message from API response
        //     // The request utility now extracts both 'error' and 'message' fields from API response
        //     let errorMessage = error?.message || 'Place creation failed. Please try again.';
            
        //     // Convert backend validation errors to user-friendly messages
        //     if (errorMessage.includes('social') && errorMessage.includes('value') && errorMessage.includes('required')) {
        //         errorMessage = '3 social login is required';
        //     } else if (errorMessage.includes('timeout')) {
        //         errorMessage = 'Upload timed out. Please check your internet connection and try again.';
        //     } else if (errorMessage.includes('File object missing')) {
        //         errorMessage = 'Some image files are missing. Please re-select your images and try again.';
        //     } else if (errorMessage.includes('Invalid file type')) {
        //         errorMessage = 'Please upload only image files (JPG, PNG, etc.).';
        //     } else if (errorMessage.includes('File too large')) {
        //         errorMessage = 'Some files are too large. Please use images smaller than 10MB each.';
        //     }
            
        //     setValidationError(errorMessage);
        // }




    };


    return (
        <>
        <TopNavBar title="Add Place" canGoBack={true} />
        <Paper sx={{ p: 3, mb: 30 }}>
            {/* Draft Management Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3 }}>
                {/* <Typography variant="h5">Add New Place</Typography> */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={() => navigate('/place/drafts')}>
                        <DraftIcon />
                        <Typography fontWeight={"medium"} sx={{ color: '#008387', textTransform: 'none' }}>{'Open from Draft'}</Typography>
                    </Box>
                </Box>
            </Box>

            <form onSubmit={handleSubmit} onReset={() => setForm(emptyForm)}>
                <Stack spacing={3}>
                    <MapSelector
                        onLocationSelect={(location) => {
                            handleLocationSelect(location.lat, location.lng, location.address);
                        }}
                        onLocationClear={() => {
                            setForm((prev) => ({
                                ...prev,
                                location: { lat: '', lng: '' },
                            }));
                            setCurrentLocation(null);
                            if (submitAttempted) {
                                setValidationErrors((prev) => ({
                                    ...prev,
                                    location: 'Please select a location on the map',
                                }));
                            }
                        }}
                        initialLocation={currentLocation || undefined}
                        />
                    {submitAttempted && validationErrors.location && (
                        <Typography color="error" variant="caption" sx={{ display: 'block' }}>
                            {validationErrors.location}
                        </Typography>
                    )}


                    {/* Media Section */}
                    <Box sx={{ width: '100%', maxWidth: '800px', my: 2 }}>

                        <MediaUpload
                            value={imagesData}
                            onImagesChange={(images: any) => {
                                console.log('images outside', images);
                                setImagesData(images);
                                if (images.length > 0) {
                                    setValidationErrors((prev) => ({ ...prev, images: undefined }));
                                }
                            }}
                            maxImages={5}
                            type={'image'}
                            mediaMode={'image-only'}
                            />
                        {submitAttempted && validationErrors.images && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                {validationErrors.images}
                            </Typography>
                        )}
                    </Box>
                    <Grid container spacing={2}>
                        <Grid sx={{ width: '100%', maxWidth: '800px' }}>
                            <TextField
                                name="name"
                                label="Place Name"
                                value={form.name}
                                onChange={handleChange}
                                fullWidth
                                required
                                inputProps={{ maxLength: 100 }}
                                sx={{ minWidth: '300px' }}
                                error={submitAttempted && !!validationErrors.name}
                                helperText={submitAttempted && validationErrors.name ? validationErrors.name : ''}
                                />
                        </Grid>
                        <Grid sx={{ width: '100%', maxWidth: '800px' }}>
                            <TextField
                                name="description"
                                label="Description"
                                value={form.description}
                                onChange={handleChange}
                                multiline
                                rows={4}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                onBlur={handleDescriptionBlur}
                                error={submitAttempted && !!validationErrors.description}
                                helperText={submitAttempted && validationErrors.description ? validationErrors.description : ''}
                                />

                        </Grid>
                        <Grid sx={{ width: '100%', maxWidth: '800px' }}>
                            <TextField
                                name="address"
                                label="Address"
                                value={form.address}
                                onChange={handleChange}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                error={submitAttempted && !!validationErrors.address}
                                helperText={submitAttempted && validationErrors.address ? validationErrors.address : ''}
                                />
                        </Grid>
                    </Grid>

                    {/* Categories and Type Section */}
                    <Box>
                        <Grid container spacing={2}>
                            <Grid sx={{ width: '100%', maxWidth: '400px' }}>
                                {/* <CategoryChips
                                        categories={[...categories]}
                                        selectedCategories={form.categories}
                                        onChange={(newCategories) => setForm(prev => ({
                                            ...prev,
                                            categories: newCategories
                                        }))}
                                        maxInitialDisplay={4}
                                    /> */}
                                <CategoryChips
                                    categories={categories}
                                    selectedCategories={selectedCategories}
                                    onChange={(newCategories) => {
                                        setSelectedCategories(newCategories.map(category => category.name));
                                        setForm(prev => ({
                                            ...prev,
                                            categories: newCategories.map(category => category.name)
                                        }));
                                        if (newCategories.length > 0) {
                                            setValidationErrors((prev) => ({ ...prev, categories: undefined }));
                                        }
                                    }}
                                    maxInitialDisplay={6}
                                />
                                {submitAttempted && validationErrors.categories && (
                                    <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                        {validationErrors.categories}
                                    </Typography>
                                )}
                            </Grid>
                            {/* <Grid sx={{ width: '100%', maxWidth: '400px' }}>
                                <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Type*</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {typeOptions.map((type) => (
                                            <Chip
                                                key={type}
                                                label={type}
                                                onClick={() => setForm(prev => ({ ...prev, type: type }))}
                                                variant={form.type === type ? "filled" : "outlined"}
                                                color={form.type === type ? "primary" : "default"}
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Grid> */}
                        </Grid>
                    </Box>

                    <Divider />

                    {/* Owner Details Section */}
                    <Box>

                        <Grid container spacing={2}>
                            <TextField
                                label="Email"
                                type="email"
                                value={form.owner_details.email}
                                onChange={(e) => {
                                    setForm(prev => ({
                                        ...prev,
                                        owner_details: { ...prev.owner_details, email: e.target.value }
                                    }));
                                    setValidationErrors((prev) => ({ ...prev, email: undefined }));
                                }}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                error={submitAttempted && !!validationErrors.email}
                                helperText={submitAttempted && validationErrors.email ? validationErrors.email : ''}
                            />
                            <TextField
                                label="Owner Name"
                                value={form.owner_details.name}
                                onChange={(e) => {
                                    setForm(prev => ({
                                        ...prev,
                                        owner_details: { ...prev.owner_details, name: e.target.value }
                                    }));
                                    setValidationErrors((prev) => ({ ...prev, ownerName: undefined }));
                                }}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                error={submitAttempted && !!validationErrors.ownerName}
                                helperText={submitAttempted && validationErrors.ownerName ? validationErrors.ownerName : ''}
                            />
                            <TextField
                                label="Phone"
                                value={form.owner_details.phone}
                                onChange={(e) => {
                                    setForm(prev => ({
                                        ...prev,
                                        owner_details: { ...prev.owner_details, phone: e.target.value.replace(/\D/g, '') }
                                    }));
                                    setValidationErrors((prev) => ({ ...prev, phone: undefined }));
                                }}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                error={submitAttempted && !!validationErrors.phone}
                                helperText={submitAttempted && validationErrors.phone ? validationErrors.phone : ''}
                            />
                            <TextField
                                label="Whatsapp"
                                value={form.owner_details.whatsapp}
                                onChange={(e) => {
                                    setForm(prev => ({
                                        ...prev,
                                        owner_details: { ...prev.owner_details, whatsapp: e.target.value.replace(/\D/g, '') }
                                    }));
                                    setValidationErrors((prev) => ({ ...prev, whatsapp: undefined }));
                                }}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                error={submitAttempted && !!validationErrors.whatsapp}
                                helperText={submitAttempted && validationErrors.whatsapp ? validationErrors.whatsapp : ''}
                            />
                        </Grid>
                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                            <TextField
                                label="Website"
                                value={form.social.find(item => item.key === 'website')?.value}
                                // onChange={(e) => setForm(prev => ({
                                //     ...prev,
                                //     social: prev.social.map(item => item.key === 'website' ? { ...item, value: e.target.value } : item)
                                // }))}
                                onChange={(e: any) => {
                                    setForm(prev => ({
                                        ...prev,
                                        social: prev.social.map(item => item.key === 'website' ? { ...item, value: e.target.value } : item)
                                    }));
                                }}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Facebook"
                                value={form.social.find(item => item.key === 'facebook')?.value}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    social: prev.social.map(item => item.key === 'facebook' ? { ...item, value: e.target.value } : item)
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Instagram"
                                value={form.social.find(item => item.key === 'instagram')?.value}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    social: prev.social.map(item => item.key === 'instagram' ? { ...item, value: e.target.value } : item)
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Youtube"
                                value={form.social.find(item => item.key === 'youtube')?.value}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    social: prev.social.map(item => item.key === 'youtube' ? { ...item, value: e.target.value } : item)
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />

                        </Grid>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Server / unmapped errors (inline fields above when possible) */}
                        {validationErrors.general && (
                            <Box
                                sx={{
                                    backgroundColor: '#FFEBEE',
                                    color: '#C62828',
                                    padding: 2,
                                    borderRadius: 2,
                                    border: '1px solid #EF9A9A',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: '#C62828',
                                        fontSize: '14px',
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {validationErrors.general}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                            variant="outlined"
                            onClick={handleSaveDraft}
                            sx={{
                                borderColor: '#FF6B35',
                                color: '#FF6B35',
                                py: 1,
                                borderRadius: '25px',
                                textTransform: 'none',
                                fontSize: '16px',
                                fontWeight: 'bold',
                            }}
                        >
                            {isSavingDraft ? 'Saving Draft…' : 'Save as Draft'}
                        </Button>
                            <Button
                                variant="contained"
                                type="submit"
                                size="large"
                                sx={{ backgroundColor: '#F57C00', color: 'white', borderRadius: '100px' }}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </Stack>
            </form>

        </Paper>
        </>

    );
};

export default PlaceForm;
